import { jsPDF } from 'jspdf';

interface Issue {
    id: number;
    type: string;
    severity: 'high' | 'medium' | 'low';
    confidence: number;
    description: string;
    evidence_path: string;
}

interface Screenshot {
    id: number;
    path: string;
}

interface Scan {
    id: number;
    session_id: number;
    url: string;
    device: string;
    status: string;
    started_at: string;
    completed_at: string;
}

interface ConsoleLog {
    type: 'log' | 'warn' | 'error' | 'info' | 'debug';
    text: string;
    timestamp: number;
}

interface Cookie {
    name: string;
    value: string;
    domain: string;
    httpOnly: boolean;
    secure: boolean;
}

interface PerformanceMetrics {
    domContentLoaded: number | null;
    loadComplete: number | null;
    firstPaint: number | null;
    firstContentfulPaint: number | null;
    domNodes: number;
}

interface DebugData {
    consoleLogs?: ConsoleLog[];
    cookies?: Cookie[];
    localStorage?: Record<string, string>;
    sessionStorage?: Record<string, string>;
    performanceMetrics?: PerformanceMetrics;
}

export interface ReportData {
    scan: Scan;
    issues: Issue[];
    screenshots: Screenshot[];
    summary: {
        executive: string;
        issueCount: { high: number; medium: number; low: number };
        totalIssues: number;
    };
}

export const generateReportPDF = async (report: ReportData, debugData?: DebugData | null) => {
    const doc = new jsPDF();
    const { scan, issues, screenshots, summary } = report;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = 20;

    // Helper to add text and advance Y
    const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');

        const splitText = doc.splitTextToSize(text, pageWidth - (margin * 2));
        doc.text(splitText, margin, yPos);
        yPos += (splitText.length * fontSize * 0.5) + 5;
    };

    const checkPageBreak = (neededSpace: number = 20) => {
        if (yPos + neededSpace > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPos = 20;
        }
    };

    // --- Header ---
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('AUTOPSYURL DIAGNOSTIC REPORT', margin, yPos);
    yPos += 15;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // Scan Details
    addText(`URL: ${scan.url}`);
    addText(`Device: ${scan.device}`);
    addText(`Scan Date: ${new Date(scan.started_at).toLocaleString()}`);
    yPos += 5;

    // --- Executive Summary ---
    doc.setDrawColor(0);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;

    addText('EXECUTIVE SUMMARY', 14, true);
    addText(summary.executive);
    yPos += 5;

    // Stats
    addText(`Total Issues: ${summary.totalIssues} (High: ${summary.issueCount.high}, Medium: ${summary.issueCount.medium}, Low: ${summary.issueCount.low})`);
    yPos += 10;

    // --- Performance Metrics ---
    if (debugData?.performanceMetrics) {
        checkPageBreak(40);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        addText('PERFORMANCE METRICS', 14, true);
        const pm = debugData.performanceMetrics;
        addText(`DOM Content Loaded: ${pm.domContentLoaded ? `${pm.domContentLoaded}ms` : 'N/A'}`);
        addText(`Load Complete: ${pm.loadComplete ? `${pm.loadComplete}ms` : 'N/A'}`);
        addText(`First Paint: ${pm.firstPaint ? `${Math.round(pm.firstPaint)}ms` : 'N/A'}`);
        addText(`First Contentful Paint: ${pm.firstContentfulPaint ? `${Math.round(pm.firstContentfulPaint)}ms` : 'N/A'}`);
        addText(`DOM Nodes: ${pm.domNodes}`);
        yPos += 5;
    }

    // --- Visual Evidence ---
    if (screenshots.length > 0) {
        checkPageBreak(50);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        addText('VISUAL EVIDENCE', 14, true);

        for (const screenshot of screenshots) {
            try {
                // Fetch image to get blob/base64
                const imageUrl = `http://localhost:3001${screenshot.path}`;
                const response = await fetch(imageUrl);
                const blob = await response.blob();

                // Convert to base64
                const base64 = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(blob);
                });

                // Add to PDF
                const imgProps = doc.getImageProperties(base64);
                const imgWidth = pageWidth - (margin * 2);
                const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

                checkPageBreak(imgHeight + 10);
                doc.addImage(base64, 'JPEG', margin, yPos, imgWidth, imgHeight);
                yPos += imgHeight + 10;

            } catch (err) {
                console.error('Failed to load screenshot for PDF', err);
                addText(`[Error loading screenshot: ${screenshot.path}]`);
            }
        }
        yPos += 10;
    }

    // --- Detailed Findings ---
    checkPageBreak(30);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    addText('DETAILED FINDINGS', 14, true);

    if (issues.length === 0) {
        addText('No significant issues detected.');
    } else {
        issues.forEach((issue, index) => {
            checkPageBreak(40);
            const severityColor = issue.severity === 'high' ? '#dc3545' : issue.severity === 'medium' ? '#fd7e14' : '#6c757d';
            doc.setTextColor(severityColor);
            addText(`[${index + 1}] ${issue.type.replace(/_/g, ' ').toUpperCase()} (${issue.severity.toUpperCase()})`, 11, true);
            doc.setTextColor(0, 0, 0);
            addText(`Confidence: ${issue.confidence}%`);
            addText(issue.description);
            if (issue.evidence_path) {
                addText(`Evidence: ${issue.evidence_path}`);
            }
            yPos += 5;
        });
    }

    // --- Console Logs ---
    if (debugData?.consoleLogs && debugData.consoleLogs.length > 0) {
        checkPageBreak(40);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        addText(`CONSOLE LOGS (${debugData.consoleLogs.length} entries)`, 14, true);

        const logsToShow = debugData.consoleLogs.slice(0, 20); // Limit to 20 for PDF
        logsToShow.forEach(log => {
            checkPageBreak(15);
            addText(`[${log.type.toUpperCase()}] ${log.timestamp}ms: ${log.text.slice(0, 100)}${log.text.length > 100 ? '...' : ''}`);
        });
        if (debugData.consoleLogs.length > 20) {
            addText(`... and ${debugData.consoleLogs.length - 20} more entries (see debug-data.json)`);
        }
        yPos += 5;
    }

    // --- Cookies ---
    if (debugData?.cookies && debugData.cookies.length > 0) {
        checkPageBreak(40);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
        addText(`COOKIES (${debugData.cookies.length} cookies)`, 14, true);

        debugData.cookies.slice(0, 15).forEach(cookie => {
            checkPageBreak(10);
            addText(`${cookie.name} (${cookie.domain}) ${cookie.secure ? '[Secure]' : ''} ${cookie.httpOnly ? '[HttpOnly]' : ''}`);
        });
        if (debugData.cookies.length > 15) {
            addText(`... and ${debugData.cookies.length - 15} more cookies`);
        }
        yPos += 5;
    }

    // --- Footer ---
    checkPageBreak(20);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    addText(`Report generated: ${new Date().toLocaleString()}`, 9);
    addText('Generated by autopsyURL - Clinical Web Debugging', 9);

    // Save
    doc.save(`autopsyurl-report-${new URL(scan.url).hostname}-${Date.now()}.pdf`);
};

